import React from 'react';
import cx from 'classnames';
import { arrayify } from '../../utils/array/array';
import Badge from './Badge';
import mappings from './mappings';
import styles from './StatusBadges.module.scss';

interface SummaryShape {
  hasSubCategories?: boolean;
  count?: number;
}

interface StatusBadgesProps {
  badges: string | string[];
  className?: string;
  compact?: boolean;
  showTooltip?: boolean;
  summaries?: {
    unpublished?: SummaryShape;
    untranslated?: SummaryShape;
    deleted?: SummaryShape;
    moved?: SummaryShape;
  };
}

const summaryConfigs = {
  moved: {
    className: styles.movedInverted,
    labelFn: (count: number) => `${count} moved`,
    tooltip: 'Moved',
  },
  untranslated: {
    className: styles.translationInverted,
    labelFn: (count: number) => `${count} translations`,
    tooltip: 'Untranslated',
  },
  unpublished: {
    className: styles.publishInverted,
    labelFn: (count: number) => `${count} publishing`,
    tooltip: 'Unpublished',
  },
  deleted: {
    className: styles.deletedInverted,
    labelFn: (count: number) => `${count} deleted`,
    tooltip: 'Deleted',
  },
} as const;

type SummaryKey = keyof typeof summaryConfigs;

const summaryOrder: SummaryKey[] = [
  'moved',
  'untranslated',
  'unpublished',
  'deleted',
];

const StatusBadges: React.FC<StatusBadgesProps> = ({
  badges,
  className,
  compact = false,
  showTooltip = true,
  summaries = {},
}) => (
  <div className={cx(styles.badges, { [styles.compact]: compact }, className)}>
    {summaryOrder.map((key) => {
      const summary = summaries[key];
      if (!summary?.hasSubCategories || summary.count === undefined) return null;
      const config = summaryConfigs[key];
      return (
        <Badge
          key={key}
          className={config.className}
          compact={compact}
          label={config.labelFn(summary.count)}
          labelCompact={summary.count.toString()}
          tooltip={config.tooltip}
          showTooltip={showTooltip}
        />
      );
    })}
    {arrayify(badges).map((badge) => {
      const MappedBadge = mappings[badge];
      return MappedBadge ? (
        <MappedBadge key={badge} compact={compact} showTooltip={showTooltip} />
      ) : null;
    })}
  </div>
);

export default StatusBadges;
